import { Transition } from '@headlessui/react'
import { useContext, useEffect, useRef, useState, useCallback } from "react";
import { deleteData, OAuthError, patchData, postData, putData } from "../backend";
import { WorkflowContext, useModal } from "../contexts";
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useUser from "../hooks/useUser";
import OAuthModal from './OAuthModal';
import OAuthModalMicrosoft from './OAuthModalMicrosoft';
import WorkflowSelectModal from './WorkflowSelectModal';
import TransitionWorkflowPage from './transitions/TransitionWorkflowPage';
import WorkflowSignatureRequestSidebar from './WorkflowSignatureRequestSidebar';
import EmailEditor, { AttachmentAction, Ref as EmailEdtorRef } from './EmailEditor';
import SignatureRequestEmailRecipients from './SignatureRequestEmailRecipients';
import SignatureRequestEmailContant from './SignatureRequestEmailContant';
import { AbstractEmail, Attachment, EmailType, SignatureConfig } from '../interfaces';
import { ORDINAL_MAP } from '../utils';
import SignatureRequestModalRevertOverride from './SignatureRequestModalRevertOverride';

function WorkflowSignatureRequest() {
  const { hasPermission } = useUser();
  const { t } = useTranslation();
  const { url } = useRouteMatch();

  const history = useHistory();
  const { pathname, search } = useLocation();

  // When `n` is present we are editing the email override for that recipient
  const queryRecipientNumber = parseInt(new URLSearchParams(search).get("n") ?? "") || null;

  const workflowContext = useContext(WorkflowContext);
  const { workflow, mutateWorkflow } = workflowContext;

  const [signatureConfig, setSignatureConfig] = useState<SignatureConfig>();
  const [hasCheckedScopes, setHasCheckedScopes] = useState(false);

  const { openModal } = useModal();

  const emailEditorRef = useRef<EmailEdtorRef>(null);

  const checkScopesForEmailType = useCallback(async (emailType: EmailType) => {
    if (emailType === "OUTLOOK") {
      try {
        await postData("/microsoft/access-token/");
      }
      catch (error) {
        if (error instanceof OAuthError)
          openModal(<OAuthModalMicrosoft oauthUrl={error.oauthUrl} />);

        return false;
      }
    }

    if (emailType === "GMAIL") {
      try {
        await postData("/auth/scopes/", { scopes: ["https://www.googleapis.com/auth/gmail.send"] });
      }
      catch (error) {
        if (error instanceof OAuthError)
          openModal(<OAuthModal title={t('Portant requires your permission to send emails')} oauthUrl={error.oauthUrl} />);

        return false;
      }
    }

    return true;
  }, [openModal, t]);


  useEffect(() => {
    if (workflow.signatureConfig !== null)
      setSignatureConfig(workflow.signatureConfig);
    else
      setSignatureConfig(undefined);
  }, [workflow.signatureConfig]);

  useEffect(() => {
    if (hasCheckedScopes || !signatureConfig || !pathname.endsWith("/signature-request"))
      return;

    checkScopesForEmailType(signatureConfig.emailType)
      .finally(() => setHasCheckedScopes(true));

  }, [hasCheckedScopes, openModal, pathname, t, checkScopesForEmailType, signatureConfig]);

  async function updateSignatureConfig(updates: any) {
    if (!signatureConfig)
      return;

    const updatedSignatureConfig = { ...signatureConfig!, ...updates };
    setSignatureConfig(updatedSignatureConfig);

    await putData(`/signatures/signature-configs/${updatedSignatureConfig.id}/`, updatedSignatureConfig)
      .catch(console.error);

    mutateWorkflow();
  }

  async function updateAttachment(action: AttachmentAction, attachmentId: string, attachment?: Partial<Attachment>) {
    if (action === "CREATE")
      return postData(`/signatures/signature-configs/${signatureConfig!.id}/attachments/`, attachment);

    else if (action === "DELETE")
      return deleteData(`/signatures/signature-config-attachments/${attachmentId}/`);

    else if (action === "UPDATE")
      return patchData(`/signatures/signature-config-attachments/${attachmentId}/`, { sharingMode: attachment!.sharingMode });
  }

  // The first recipient always uses the default email, so overrides only exist for recipients 2-4
  // Also ignore recipient numbers that no longer exist (e.g. the recipient was removed)
  const recipientNumber = (queryRecipientNumber && signatureConfig && queryRecipientNumber >= 2 && queryRecipientNumber <= signatureConfig.numRecipients)
    ? queryRecipientNumber
    : null;

  // Clicking the first or active recipient returns to the default email
  function selectRecipient(n: number) {
    const target = (n === 1 || n === recipientNumber) ? pathname : `${pathname}?n=${n}`;

    if (emailEditorRef.current)
      emailEditorRef.current.confirmLeave(() => history.push(target));
    else
      history.push(target);
  }

  const override = recipientNumber
    ? signatureConfig?.overrides.find(o => o.recipientNumber === recipientNumber)
    : undefined;

  async function updateOverrideEmail(email: AbstractEmail) {
    const updates = {
      emailCc: email.emailCc ?? "",
      emailBcc: email.emailBcc ?? "",
      emailSubject: email.emailSubject,
      emailContent: email.emailContent,
      customFormatting: email.customFormatting ?? false,
    };

    const updatedOverride = override
      ? await patchData(`/signatures/signature-config-overrides/${override.id}/`, updates)
      : await postData(`/signatures/signature-configs/${signatureConfig!.id}/overrides/`, { ...updates, recipientNumber });

    await mutateWorkflow();

    return { ...email, ...updatedOverride };
  }

  async function revertOverride(force = false) {
    if (!override)
      return;

    if (!force)
      return openModal(<SignatureRequestModalRevertOverride onConfirm={() => revertOverride(true)} />);

    await deleteData(`/signatures/signature-config-overrides/${override.id}/`)
      .catch(console.error);

    // Reset the editor to the default email, ready to create a new override if edited again
    emailEditorRef.current?.resetInitialEmail({
      ...signatureConfig!,
      id: `override-${recipientNumber}`,
    });

    await mutateWorkflow();
  }

  async function updateEmail(email: AbstractEmail) {
    if (recipientNumber)
      return updateOverrideEmail(email);

    // Only take the fields owned by the email editor, the editor's copy of the rest of the config
    // can be stale (e.g. recipients changed in the sidebar since the editor was opened)
    const emailUpdates = {
      emailType: email.emailType,
      emailFromAlias: email.emailFromAlias,
      sendFromRunner: (email as SignatureConfig).sendFromRunner,
      emailCc: email.emailCc,
      emailBcc: email.emailBcc,
      emailSubject: email.emailSubject,
      emailContent: email.emailContent,
      customFormatting: email.customFormatting,
    };

    const updatedEmail = await putData(`/signatures/signature-configs/${signatureConfig!.id}/`, { ...signatureConfig!, ...emailUpdates });

    setSignatureConfig(updatedEmail);
    mutateWorkflow({ ...workflow, signatureConfig: updatedEmail });

    return updatedEmail;
  }

  if (!signatureConfig) {
    if (!pathname.endsWith("/signature-request"))
      return null;

    return (
      <div className="flex flex-col items-center justify-center gap-4 h-full w-full">
        <div className="font-semibold font-gilroy text-gray-600 text-center w-96">
          {t("This workflow does not contain a signature request, would you like to add one?")}
        </div>
        <button className="btn btn-blue" onClick={() => openModal(<WorkflowSelectModal title={t("Add Workflow Signature Request")} subtitle={t("Add a signature request to your workflow")} showSignatureRequest workflowContext={workflowContext} />)}>
          {t("Add Signature Request")}
        </button>
      </div>
    );
  }

  // Overrides only replace the cc/bcc, subject and content, everything else comes from the base config
  // If no override exists yet, the base config is used as the starting point for the new override
  const initialEmail: AbstractEmail = recipientNumber
    ? {
      ...signatureConfig,
      id: override?.id ?? `override-${recipientNumber}`,
      emailCc: override ? override.emailCc : signatureConfig.emailCc,
      emailBcc: override ? override.emailBcc : signatureConfig.emailBcc,
      emailSubject: override ? override.emailSubject : signatureConfig.emailSubject,
      emailContent: override ? override.emailContent : signatureConfig.emailContent,
      customFormatting: override ? override.customFormatting : signatureConfig.customFormatting,
    }
    : signatureConfig;

  return (
    <>
      <TransitionWorkflowPage show={pathname.endsWith("/signature-request")}>

        {hasPermission(workflow.team, "workflows.edit") &&
          <WorkflowSignatureRequestSidebar setSignatureConfig={setSignatureConfig} signatureConfig={signatureConfig} updateSignatureConfig={updateSignatureConfig} />
        }

        <Transition.Child className="flex flex-col items-center justify-center m-auto"
          enter="transition-opacity duration-150"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <EmailEditor
            key={recipientNumber ?? "base"}
            ref={emailEditorRef}
            initialEmail={initialEmail}
            updateEmail={updateEmail}
            updateAttachment={updateAttachment}
            checkCanChangeEmailType={checkScopesForEmailType}
            onClose={() => history.push(url)}
            customTo={<SignatureRequestEmailRecipients signatureConfig={signatureConfig} activeRecipient={recipientNumber} onSelectRecipient={selectRecipient} />}
            customBodyComponent={SignatureRequestEmailContant}
            disabled={!hasPermission(workflow.team, "workflows.edit")}
            disclaimer={override ? t("This recipient has personalized content") : undefined}
            disclaimerAction={override ? { text: t("Revert to default signature request"), onClick: () => revertOverride() } : undefined}
            title={recipientNumber
              ? `${t("Request Signatures")} (${ORDINAL_MAP[recipientNumber]} ${t("Recipient")})`
              : t("Request Signatures")}
          />
        </Transition.Child>
      </TransitionWorkflowPage>
    </>
  );

}

export default WorkflowSignatureRequest;
